import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Header } from "@/components/Header";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Home, Mountain, DoorOpen, Trees, ArrowRight, ArrowLeft,
  Download, CheckCircle2, Loader2, RefreshCw, Phone, Sparkles,
  Lock, FileText, ShieldCheck, PhoneCall, Star, Sparkle, Hammer, Wrench, MapPin,
} from "lucide-react";

const TYPES = [
  { key: "maison", label: "Maison", icon: Home },
  { key: "appartement", label: "Appartement", icon: Building2 },
  { key: "terrain", label: "Terrain", icon: Trees },
  { key: "autre", label: "Autre", icon: DoorOpen },
];

const CONDITIONS = [
  { key: "neuf", label: "Refait à neuf / Excellent état", icon: Sparkle },
  { key: "bon", label: "Bon état / Habitable", icon: CheckCircle2 },
  { key: "a_rafraichir", label: "À rafraîchir", icon: Wrench },
  { key: "a_renover", label: "Travaux importants / À rénover", icon: Hammer },
];

const EXTERIORS = ["balcon", "terrasse", "jardin", "garage", "parking", "piscine"];

const DPE_OPTIONS = ["A", "B", "C", "D", "E", "F", "G", "nc"];
const DPE_COLORS = {
  A: "bg-green-600", B: "bg-green-500", C: "bg-lime-500", D: "bg-yellow-400",
  E: "bg-orange-400", F: "bg-orange-600", G: "bg-red-600",
};

const HEATING_OPTIONS = [
  { key: "gaz", label: "Gaz" },
  { key: "electrique", label: "Électrique" },
  { key: "pompe_chaleur", label: "Pompe à chaleur" },
  { key: "fioul", label: "Fioul" },
  { key: "bois", label: "Bois / Poêle" },
  { key: "collectif", label: "Collectif" },
  { key: "autre", label: "Autre" },
];

const SELL_TIMELINES = [
  { key: "asap", label: "Dès que possible / Au plus vite" },
  { key: "3_6_mois", label: "Dans les 3 à 6 mois" },
  { key: "plus_6_mois", label: "Dans plus de 6 mois" },
  { key: "curiosite", label: "Simple curiosité / Projets futurs" },
];

const TOTAL_STEPS = 5;

export default function Estimation({ embedded = false }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    property_type: "", city: "", surface: 70, rooms: 3, bedrooms: 2,
    condition: "", exterior: [], floor: "", year_built: "", land_surface: "",
    address: "", ban_city: "", ban_postcode: "", bathrooms: 1, dpe: "", heating: "", has_elevator: null,
    full_name: "", email: "", phone: "", wants_advisor: true, is_owner: null,
    sell_timeline: "", rgpd_consent: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleExt = (e) =>
    setForm((f) => ({
      ...f,
      exterior: f.exterior.includes(e) ? f.exterior.filter((x) => x !== e) : [...f.exterior, e],
    }));

  const canNext = () => {
    if (step === 0) return !!form.property_type && !!form.address.trim();
    if (step === 1) return Number(form.surface) > 0 && Number(form.rooms) > 0;
    if (step === 2) return !!form.condition;
    if (step === 3) return !!form.sell_timeline;
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));
  // Choix + passage automatique à l'étape suivante
  const choose = (k, v) => { set(k, v); setTimeout(next, 150); };

  const submit = async () => {
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Nom complet, email et téléphone sont obligatoires.");
      return;
    }
    if (!form.sell_timeline) {
      toast.error("Merci d'indiquer quand vous souhaitez vendre.");
      return;
    }
    setLoading(true);
    try {
      const isLand = form.property_type === "maison" || form.property_type === "terrain";
      const payload = {
        ...form,
        city: form.ban_city || "",
        postcode: form.ban_postcode || null,
        address: form.address.trim() || null,
        surface: Number(form.surface),
        rooms: Number(form.rooms),
        land_surface: isLand && form.land_surface !== "" ? Number(form.land_surface) : null,
        floor: null,
        year_built: null,
        bathrooms: null,
        dpe: null,
        heating: null,
        email: form.email.trim(),
        phone: form.phone.trim(),
        wants_advisor: true,
        rgpd_consent: true,
      };
      const { data } = await api.post("/estimate", payload);
      navigate("/merci", { state: { calc: data.calc, property: data.property } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const euro = (n) => new Intl.NumberFormat("fr-FR").format(n) + " €";

  if (result) return <ResultView result={result} euro={euro} embedded={embedded} wantsAdvisor={form.wants_advisor} />;

  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <div className={embedded ? "" : "App min-h-screen bg-slate-50"} data-testid="estimation-page">
      {!embedded && <Header />}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-xs font-semibold text-slate-500 mb-2">
            <span>Étape {step + 1} / {TOTAL_STEPS}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#2563EB] rounded-full"
              animate={{ width: `${progress}%` }}
              transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
              data-testid="progress-bar"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {step === 0 && (
                <>
                  <StepTitle t="Quel bien souhaitez-vous estimer ?" />
                  <div className="grid grid-cols-2 gap-3">
                    {TYPES.map((t) => (
                      <button
                        key={t.key}
                        onClick={() => set("property_type", t.key)}
                        data-testid={`type-${t.key}`}
                        className={`flex flex-col items-center justify-center gap-3 p-6 min-h-[110px] rounded-2xl border-2 transition-all ${
                          form.property_type === t.key
                            ? "border-[#2563EB] bg-blue-50"
                            : "border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        <t.icon className={`w-8 h-8 ${form.property_type === t.key ? "text-[#2563EB]" : "text-slate-500"}`} strokeWidth={1.5} />
                        <span className="text-base font-semibold text-slate-900">{t.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-6">
                    <AddressAutocomplete
                      label="Adresse du bien"
                      value={form.address}
                      onChange={(v) => { set("address", v); set("ban_city", ""); set("ban_postcode", ""); }}
                      onSelect={(info) => { set("address", info.label); set("ban_city", info.city); set("ban_postcode", info.postcode); }}
                      testid="address-input"
                    />
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <StepTitle t="Caractéristiques du bien" />
                  <TextField
                    label="Surface habitable (m²)"
                    value={form.surface}
                    onChange={(v) => set("surface", v)}
                    type="number"
                    testid="surface-input"
                  />
                  {(form.property_type === "maison" || form.property_type === "terrain") && (
                    <TextField
                      label="Surface du terrain (m²)"
                      value={form.land_surface}
                      onChange={(v) => set("land_surface", v)}
                      type="number"
                      testid="land-surface-input"
                    />
                  )}
                  <NumberField label="Nombre de pièces" value={form.rooms} onChange={(v) => set("rooms", v)} testid="rooms-input" />
                </>
              )}

              {step === 2 && (
                <>
                  <StepTitle t="Quel est l'état général de votre bien ?" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {CONDITIONS.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => choose("condition", c.key)}
                        data-testid={`condition-${c.key}`}
                        className={`flex items-center gap-3 px-5 py-5 rounded-2xl border-2 text-base font-semibold text-left transition-all ${
                          form.condition === c.key ? "border-[#2563EB] bg-blue-50 text-[#2563EB]" : "border-slate-200 hover:border-slate-400 text-slate-800"
                        }`}
                      >
                        <c.icon className={`w-6 h-6 shrink-0 ${form.condition === c.key ? "text-[#2563EB]" : "text-slate-500"}`} strokeWidth={1.8} />
                        {c.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <StepTitle t="Quand souhaitez-vous vendre ?" />
                  <div className="space-y-3">
                    {SELL_TIMELINES.map((st) => (
                      <button
                        key={st.key}
                        type="button"
                        onClick={() => choose("sell_timeline", st.key)}
                        data-testid={`sell-${st.key}`}
                        className={`w-full flex items-center gap-3 px-5 py-5 rounded-2xl border-2 text-base font-semibold text-left transition-all ${
                          form.sell_timeline === st.key ? "border-[#2563EB] bg-blue-50 text-[#2563EB]" : "border-slate-200 hover:border-slate-400 text-slate-800"
                        }`}
                      >
                        <span className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${
                          form.sell_timeline === st.key ? "border-[#2563EB] bg-[#2563EB]" : "border-slate-300 bg-white"
                        }`}>
                          {form.sell_timeline === st.key && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </span>
                        {st.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {step === 4 && (
                <>
                  <StepTitle t="Où devez-vous recevoir votre estimation ?" />
                  <p className="text-sm text-slate-500 -mt-4 mb-4">
                    Obtenez une fourchette de valeur instantanée et votre analyse détaillée.
                  </p>
                  <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 border border-amber-100 px-3 py-1.5 rounded-full text-xs font-semibold mb-6" data-testid="social-proof-badge">
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    Plus de 1 200 estimations réalisées en Auvergne-Rhône-Alpes
                  </div>
                  <TextField label="Nom complet *" value={form.full_name} onChange={(v) => set("full_name", v)} testid="name-input" />
                  <TextField label="Email *" value={form.email} onChange={(v) => set("email", v)} type="email" testid="email-input" />
                  <TextField label="Téléphone *" value={form.phone} onChange={(v) => set("phone", v)} type="tel" testid="phone-input" />
                </>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Nav */}
          {step < TOTAL_STEPS - 1 ? (
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100">
              <button
                onClick={prev}
                disabled={step === 0}
                data-testid="prev-btn"
                className="flex items-center gap-2 text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Retour
              </button>
              <button
                onClick={next}
                disabled={!canNext()}
                data-testid="next-btn"
                className="flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-medium transition-all"
              >
                Continuer <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="mt-8 pt-6 border-t border-slate-100">
              <button
                onClick={submit}
                disabled={loading}
                data-testid="submit-estimate-btn"
                className="w-full flex items-center justify-center gap-2 bg-[#2563EB] text-white hover:bg-blue-700 disabled:opacity-60 px-6 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/20"
              >
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Analyse en cours...</> : <>Obtenir mon estimation gratuite <ArrowRight className="w-5 h-5" /></>}
              </button>
              <p className="mt-3 text-xs text-slate-500 text-center flex items-center justify-center gap-1.5" data-testid="reassurance-note">
                <Lock className="w-3.5 h-3.5 shrink-0 text-slate-400" strokeWidth={2} />
                Gratuit &amp; sans engagement • Vos données sont sécurisées et ne seront jamais revendues.
              </p>
              <button
                onClick={prev}
                data-testid="prev-btn"
                className="mt-4 mx-auto flex items-center gap-2 text-slate-400 hover:text-slate-700 text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Retour
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepTitle({ t, small }) {
  return (
    <h2 className={`font-display font-bold text-slate-900 mb-6 ${small ? "text-lg" : "text-2xl"}`}>
      {t}
    </h2>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="flex items-center gap-3">
        <button onClick={() => onChange(Math.max(0, Number(value) - 1))} className="w-9 h-9 rounded-lg border border-slate-200 hover:border-slate-400 text-lg font-medium" data-testid={`${testid}-minus`}>−</button>
        <span className="w-8 text-center font-bold text-slate-900" data-testid={testid}>{value}</span>
        <button onClick={() => onChange(Number(value) + 1)} className="w-9 h-9 rounded-lg border border-slate-200 hover:border-slate-400 text-lg font-medium" data-testid={`${testid}-plus`}>+</button>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none"
      />
    </div>
  );
}

function AddressAutocomplete({ label, value, onChange, onSelect, testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleChange = (v) => {
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(v)}&type=housenumber&limit=5`
        );
        const data = await res.json();
        const feats = data.features || [];
        setSuggestions(feats);
        setOpen(feats.length > 0);
      } catch {
        setSuggestions([]);
        setOpen(false);
      }
    }, 250);
  };

  const pick = (f) => {
    const p = f.properties || {};
    onSelect({ label: p.label || "", city: p.city || "", postcode: p.postcode || "" });
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className="mb-4 relative" ref={boxRef}>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        data-testid={testid}
        autoComplete="off"
        placeholder="Commencez à taper votre adresse..."
        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none"
      />
      {open && suggestions.length > 0 && (
        <ul
          data-testid="address-suggestions"
          className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
        >
          {suggestions.map((f, i) => (
            <li key={f.properties.id || i}>
              <button
                type="button"
                onClick={() => pick(f)}
                data-testid={`address-suggestion-${i}`}
                className="w-full flex items-start gap-2.5 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-blue-50 transition-colors"
              >
                <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-[#2563EB]" strokeWidth={2} />
                <span>{f.properties.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ResultView({ result, euro, embedded, wantsAdvisor }) {
  const { calc, report, id, property } = result;
  const [unlocked, setUnlocked] = useState(wantsAdvisor === true);
  const sections = report.split("\n\n").map((b) => {
    const [head, ...rest] = b.split("\n");
    return { head: head.trim(), body: rest.join("\n").trim() };
  });

  return (
    <div className={embedded ? "" : "App min-h-screen bg-slate-50"} data-testid="result-view">
      {!embedded && <Header />}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center gap-2 text-[#2563EB] mb-3">
            <CheckCircle2 className="w-5 h-5" /> <span className="text-sm font-semibold uppercase tracking-wider">Votre estimation</span>
          </div>

          {/* OPTION A — Fourchette gratuite, sans engagement */}
          <div className="bg-slate-900 grain relative overflow-hidden rounded-3xl p-8 sm:p-12 text-center text-white">
            <div className="text-sm text-slate-400 mb-2 capitalize">
              {property.property_type} · {property.surface} m² · {property.city}
            </div>
            <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="estimate-range">
              {euro(calc.estimate_low)} <span className="text-slate-500 font-normal">—</span> {euro(calc.estimate_high)}
            </div>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-xs font-medium text-slate-200">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Estimation gratuite &amp; 100% sans engagement
            </div>
          </div>

          {!unlocked ? (
            /* REFUS — On repropose d'affiner l'avis de valeur gratuitement */
            <div className="mt-6 bg-white border-2 border-[#2563EB]/20 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="unlock-block">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#2563EB]" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-extrabold text-slate-900 leading-snug">
                    Affiner mon avis de valeur — gratuit &amp; sans engagement
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Accédez au bilan complet de votre avis de valeur, son analyse détaillée du marché local
                    et le dossier PDF téléchargeable.
                  </p>
                </div>
              </div>
              <ul className="mt-5 space-y-2.5">
                {["Bilan complet de votre avis de valeur", "Analyse détaillée du marché de votre ville",
                  "Dossier d'expertise en PDF téléchargeable", "Affinage gratuit avec un conseiller local (sans engagement)"].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { api.post(`/valuations/${id}/unlock`).catch(() => {}); setUnlocked(true); }}
                data-testid="unlock-btn"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-[#2563EB] text-white hover:bg-blue-700 px-6 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/20"
              >
                <Lock className="w-4 h-4" /> Affiner mon avis de valeur gratuitement
              </button>
              <p className="mt-3 text-xs text-slate-400 text-center">Gratuit &amp; sans engagement — vos coordonnées ont déjà été enregistrées.</p>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="unlocked-content">
              <a
                href={`${API}/valuations/${id}/pdf`}
                target="_blank" rel="noreferrer"
                data-testid="download-pdf-btn"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-[#2563EB] text-white hover:bg-blue-700 px-6 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/20"
              >
                <Download className="w-5 h-5" /> Télécharger le dossier PDF
              </a>

              <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="ai-report">
                <h3 className="font-display text-xl font-bold text-slate-900 mb-5">Analyse détaillée</h3>
                <div className="space-y-5">
                  {sections.map((s, i) =>
                    s.head.toUpperCase() === s.head && s.head.length < 40 && s.body ? (
                      <div key={i}>
                        <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#2563EB] mb-1.5">{s.head}</div>
                        <p className="text-slate-600 leading-relaxed whitespace-pre-line">{s.body}</p>
                      </div>
                    ) : (
                      <p key={i} className="text-slate-600 leading-relaxed whitespace-pre-line">{s.head} {s.body}</p>
                    )
                  )}
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3 bg-[#C85A40]/5 border border-[#C85A40]/20 rounded-2xl p-4" data-testid="advisor-callback">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-[#C85A40]/10 flex items-center justify-center">
                  <PhoneCall className="w-5 h-5 text-[#C85A40]" />
                </div>
                <p className="text-sm text-slate-600">
                  Un conseiller local vous recontacte gratuitement pour affiner ce prix — sans aucun engagement.
                </p>
              </div>
            </motion.div>
          )}

          <div className="mt-8 text-center">
            <button onClick={() => window.location.reload()} data-testid="new-estimate-btn" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 font-medium transition-colors">
              <RefreshCw className="w-4 h-4" /> Nouvelle estimation
            </button>
          </div>
          <p className="mt-6 text-xs text-slate-400 text-center max-w-lg mx-auto">
            Cet avis de valeur est fourni à titre indicatif et ne constitue pas une expertise réglementée.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
